import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  Database,
  useDatabasesNames,
} from "services/databases/useDatabasesNames";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";
import { GrantPrivilegesSelector } from "./GrantPrivilegesSelector";
import { GroupInner } from "./GroupInner";
import { ALL_DATABASES } from "./constant";
import { usePrivilegesHandlers } from "./usePrivilegesHandlers";
import { getOtherGroupsObjects } from "./utils";

import styles from "./styles.module.scss";

const privileges = ["MODIFY", "USAGE", "USAGE ANY SCHEMA", "VACUUM ANY"];

type Props = {
  index: number;
  resource: string;
};

export const DatabasesGroup = (props: Props) => {
  const { index, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const databases = useDatabasesNames();
  const isViewOnly = useViewOnly();
  const { t } = useTranslation();
  const menu = useMenu();

  const groupState = privilegesState.database[index];

  const otherGroupsObjects = getOtherGroupsObjects(
    privilegesState.database,
    index
  );

  const { objects, expanded } = groupState;

  const dependencies = useRef({
    index,
    privilegesDispatch,
    resource,
    objects,
  });

  const {
    onGrantPrivileges,
    onSelectResourceObjects,
    deleteGroup,
    handleMore,
  } = usePrivilegesHandlers({ index, resource });

  useEffect(() => {
    const { index, privilegesDispatch, resource, objects } =
      dependencies.current;
    if (!objects.length) {
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource,
        objects: [ALL_DATABASES],
        index,
      });
    }
  }, [dependencies]);

  const menuItems = [
    [
      ...(objects.includes(ALL_DATABASES)
        ? []
        : [
            {
              key: "add_remaining",
              text: t("roles_wizard.privileges.menu.add_remaining_databases"),
              action: () => {
                privilegesDispatch({
                  type: ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS,
                  resource,
                  allObjects: databases.map(({ catalogName }) => catalogName),
                });
              },
              testId: "roles-wizard-add-remaining-databases",
              disabled: isViewOnly,
            },
          ]),
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [ALL_DATABASES],
          });
        },
        testId: "roles-wizard-add-databases-group",
        disabled: isViewOnly,
      },
    ],

    ...(privilegesState.database.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-databases-group",
              disabled: isViewOnly,
            },
          ],
        ]
      : []),
  ];

  const prefixItems = [
    <ContextMenuItem
      disabled={isViewOnly}
      value={ALL_DATABASES}
      key={ALL_DATABASES}
      checked={objects.includes(ALL_DATABASES)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_databases")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() =>
        onSelectResourceObjects(
          objects.includes(ALL_DATABASES) ? [] : [ALL_DATABASES]
        )
      }
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const renderItem = (database: Database) => {
    const { catalogName } = database;
    const disabled = otherGroupsObjects[catalogName];
    const disabledTooltip =
      disabled && !isViewOnly
        ? t("roles_wizard.privileges.disabled_database_tooltip", {
            database: catalogName,
          })
        : undefined;
    return (
      <ContextMenuItem
        value={catalogName}
        key={catalogName}
        checked={objects.includes(catalogName)}
        disabled={disabled || isViewOnly}
        disabledTooltip={disabledTooltip}
        checkedIconPlaceholder={true}
        text={catalogName}
        direction="left"
        onClick={() => {
          objects.includes(catalogName)
            ? onSelectResourceObjects(
                objects
                  .filter(obj => obj !== catalogName)
                  .filter(obj => obj !== ALL_DATABASES)
              )
            : onSelectResourceObjects(
                [...objects, catalogName].filter(obj => obj !== ALL_DATABASES)
              );
        }}
      />
    );
  };

  const privilegesOptions = privileges.map(privilege => {
    return {
      value: privilege,
      text: formatPrivilege(privilege),
    };
  });

  return (
    <div className={styles.group}>
      <GroupInner
        expanded={expanded}
        handleMore={handleMore}
        objects={objects}
        items={databases}
        prefixItems={prefixItems}
        renderItem={renderItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_database")}
        searchKey="catalogName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
